import { AddIntegrationChannelDrawerFooter } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer/AddIntegrationChannelDrawerFooter';
import { AddIntegrationChannelDrawerHeader } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer/AddIntegrationChannelDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { SelectIntegrationWorkspace } from '@modules/integrationsV2/components/SelectIntegrationWorkspace';
import {
  useFetchTypeformForms,
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import { AddIntegrationChannelDrawerContentProps } from '@modules/integrationsV2/types';
import { getDescriptionMapInputForIntegrationChannel } from '@modules/integrationsV2/utils';
import { Autocomplete, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDescriptionInput,
  TypeformForm,
  TypeformFormInput,
} from 'generated/graphql';
import { useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';

export const AddTypeformIntegrationChannelDrawerContent = ({
  integrationID,
  integrationType,
  integrationData,
  onDrawerClose,
  channelDescriptions,
}: AddIntegrationChannelDrawerContentProps) => {
  const [selectedForm, setSelectedForm] = useState<TypeformForm | null>(null);
  const [selectedWorkspaceID, setSelectedWorkspaceID] = useState(integrationID);
  const [descriptionInput, setDescriptionInput] = useState('');

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.Typeform,
      integrationType,
    });

  const { data: forms, status: typeformFormsStatus } =
    useFetchTypeformForms(selectedWorkspaceID);

  const existingForms = integrationData.typeform?.includedForms ?? [];

  const [
    validateForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.Typeform,
    integrationType,
    hasValidFormFields: !!selectedForm,
    descriptionInput,
    currentDescription: '',
  });

  const integrateTypeformForm = async (
    selectedForm: TypeformFormInput | null,
    addAnother = true,
  ) => {
    const isValid = await validateForm();

    if (!isValid || !selectedForm) {
      return;
    }

    let updateDesciptionInput: IntegrationDescriptionInput | undefined;

    if (descriptionInput) {
      updateDesciptionInput = {
        descriptionMap: getDescriptionMapInputForIntegrationChannel(
          channelDescriptions,
          {
            description: descriptionInput.trim(),
            sourceID: selectedForm.id,
          },
        ),
      };
    }

    // Remove duplicates
    const includedForms = [
      ...new Map(
        [...existingForms, selectedForm].map((form) => [form.id, form]),
      ).values(),
    ];

    const { errors } = await updateIntegration({
      integrationID: selectedWorkspaceID,
      isActive: true,
      updateData: {
        typeform: {
          includedForms,
        },
      },
      updateDescription: updateDesciptionInput,
    });

    if (!!errors?.length) {
      return;
    }

    if (!addAnother) {
      onDrawerClose();
      return;
    }

    setSelectedForm(null);
    setDescriptionInput('');
  };

  const addedFormIDsSet = new Set(existingForms.map((f) => f.id));

  const options = forms.filter((form) => !addedFormIDsSet.has(form.id));

  return (
    <Stack height="100%" justifyContent="space-between">
      <Stack>
        <AddIntegrationChannelDrawerHeader title="Integrate a Typeform Form" />
        <Stack spacing={4} p={3}>
          <SelectIntegrationWorkspace
            selectedWorkspaceID={selectedWorkspaceID}
            onChange={(workspaceID) => setSelectedWorkspaceID(workspaceID)}
            channelType={IntegrationChannelType.Typeform}
            integrationType={integrationType}
          />
          <Autocomplete
            fullWidth
            multiple={false}
            value={selectedForm}
            options={options}
            getOptionLabel={(option) => option.title}
            onChange={(_, selected) => selected && setSelectedForm(selected)}
            renderInput={(params) => (
              <TextField {...params} label="Select Typeform form" required />
            )}
            componentsProps={{ paper: { elevation: 8 } }}
            loading={IsLoading(typeformFormsStatus)}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            clearIcon={null}
          />
          <IntegrationDescriptionForm
            value={descriptionInput}
            onChange={setDescriptionInput}
            isRequired={isDescriptionRequired}
            validatedDescriptionMessage={validatedDescriptionMessage}
            descriptionValidForQuestions={descriptionValidForQuestions}
            isValidationInProgress={isValidatingDescription}
            isPreValidatedDescription={isPreValidatedDescription}
            integrationType={integrationType}
          />
        </Stack>
      </Stack>
      <AddIntegrationChannelDrawerFooter
        onDrawerClose={onDrawerClose}
        onIntegrateAndAddAnother={() =>
          integrateTypeformForm(selectedForm, true)
        }
        onIntegrate={() => integrateTypeformForm(selectedForm)}
        isIntegrating={IsLoading(updateIntegrationStatus)}
        isDisabled={isDisabled}
        shouldOpenCancelConfirmationDialog={
          !!selectedForm || !!descriptionInput
        }
      />
    </Stack>
  );
};
